import React, { useState } from "react";
import { FiExternalLink, FiArrowLeft, FiInfo, FiHelpCircle, FiBook, FiHeadphones, FiMessageSquare, FiVideo } from "react-icons/fi";

export default function HelpView() {
  const [selectedCategory, setSelectedCategory] = useState("general");
  const [searchQuery, setSearchQuery] = useState("");

  const helpCategories = [
    { id: "general", name: "Guía General", icon: <FiInfo /> },
    { id: "dashboard", name: "Uso del Dashboard", icon: <FiHelpCircle /> },
    { id: "documents", name: "Documentos Legales", icon: <FiBook /> },
    { id: "support", name: "Soporte Técnico", icon: <FiHeadphones /> },
    { id: "faq", name: "Preguntas Frecuentes", icon: <FiMessageSquare /> },
    { id: "tutorials", name: "Tutoriales", icon: <FiVideo /> },
  ];

  const helpContent = {
    general: [
      {
        title: "Bienvenido a Lawfeed",
        content: "Lawfeed es una plataforma diseñada para ayudar a bufetes de abogados en el área mercantil a gestionar sus casos, documentos y comunicaciones de manera eficiente.",
        link: "/docs/introduction"
      },
      {
        title: "Primeros pasos",
        content: "Configure su perfil, invite a su equipo y comience a crear su primera carpeta de casos.",
        link: "/docs/getting-started"
      },
      {
        title: "Funciones principales",
        content: "Explore las principales funcionalidades: gestión de documentos, calendario, chat con clientes, y más.",
        link: "/docs/main-features"
      }
    ],
    dashboard: [
      {
        title: "Navegación en el Dashboard",
        content: "Aprenda a navegar por las diferentes secciones del dashboard y personalizar su vista.",
        link: "/docs/dashboard-navigation"
      },
      {
        title: "Configuración de accesos rápidos",
        content: "Personalice los accesos rápidos para sus funciones más utilizadas.",
        link: "/docs/quick-access"
      }
    ],
    documents: [
      {
        title: "Gestión de documentos legales",
        content: "Aprenda a crear, editar y compartir documentos legales con su equipo y clientes.",
        link: "/docs/legal-documents"
      },
      {
        title: "Plantillas de documentos",
        content: "Utilice plantillas predefinidas para agilizar la creación de documentos legales comunes.",
        link: "/docs/document-templates"
      }
    ],
    support: [
      {
        title: "Contactar soporte técnico",
        content: "Obtenga ayuda de nuestro equipo de soporte técnico a través de chat, correo electrónico o llamada.",
        link: "/support"
      },
      {
        title: "Reportar un problema",
        content: "Informe sobre problemas técnicos o bugs que encuentre en la plataforma.",
        link: "/support/report-issue"
      }
    ],
    faq: [
      {
        title: "¿Cómo puedo compartir documentos con clientes?",
        content: "Los documentos pueden compartirse directamente desde la sección de carpetas utilizando el botón 'Compartir'.",
        link: "/faq/sharing-documents"
      },
      {
        title: "¿Cómo configurar notificaciones?",
        content: "Acceda a la configuración de notificaciones desde el menú de 'Configuración' en el sidebar.",
        link: "/faq/notifications"
      },
      {
        title: "¿Cómo integrar calendario con Google Calendar?",
        content: "Vaya a 'Integraciones' y seleccione Google Calendar para configurar la sincronización.",
        link: "/faq/google-calendar"
      }
    ],
    tutorials: [
      {
        title: "Tutorial: Crear un nuevo caso",
        content: "Aprenda a crear y gestionar un nuevo caso paso a paso.",
        link: "/tutorials/new-case",
        video: true
      },
      {
        title: "Tutorial: Gestión de clientes",
        content: "Aprenda a añadir, editar y gestionar información de clientes.",
        link: "/tutorials/client-management",
        video: true
      }
    ]
  };

  const filteredContent = selectedCategory ? 
    helpContent[selectedCategory].filter(item => 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      item.content.toLowerCase().includes(searchQuery.toLowerCase())
    ) : 
    Object.values(helpContent).flat().filter(item => 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      item.content.toLowerCase().includes(searchQuery.toLowerCase())
    );

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="p-6 border-b">
        <h1 className="text-2xl font-bold text-navy-900 flex items-center">
          <FiHelpCircle className="mr-2" /> Centro de Ayuda
        </h1>
        <p className="text-gray-600 mt-2">
          Encuentre respuestas a sus preguntas y aprenda a utilizar todas las funciones de Lawfeed.
        </p>
        <div className="mt-4">
          <input
            type="text"
            placeholder="Buscar ayuda..."
            className="w-full p-2 border rounded-md"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-col md:flex-row">
        {/* Sidebar de categorías */}
        <div className="w-full md:w-64 p-4 border-r">
          <h2 className="text-lg font-semibold mb-4">Categorías</h2>
          <ul className="space-y-2">
            {helpCategories.map((category) => (
              <li key={category.id}>
                <button
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center w-full p-2 rounded-md ${
                    selectedCategory === category.id
                      ? "bg-navy-100 text-navy-900"
                      : "hover:bg-gray-100"
                  }`}
                >
                  <span className="mr-2">{category.icon}</span>
                  {category.name}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Contenido principal */}
        <div className="flex-1 p-6">
          {filteredContent.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2">
              {filteredContent.map((item, index) => (
                <div key={index} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <h3 className="text-lg font-semibold mb-2 flex items-center">
                    {item.video && <FiVideo className="mr-2 text-navy-600" />}
                    {item.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{item.content}</p>
                  <a
                    href={item.link}
                    className="text-navy-600 hover:text-navy-800 flex items-center"
                  >
                    Ver más <FiExternalLink className="ml-1" />
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <FiInfo className="mx-auto text-4xl text-gray-400 mb-4" />
              <h3 className="text-lg font-medium">No se encontraron resultados</h3>
              <p className="text-gray-500">
                Intente con otros términos de búsqueda o seleccione una categoría diferente.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="p-6 border-t bg-gray-50">
        <h2 className="text-lg font-semibold mb-4">¿No encuentra lo que busca?</h2>
        <div className="flex flex-col md:flex-row gap-4">
          <a
            href="/support/contact"
            className="flex items-center justify-center bg-navy-600 text-white py-2 px-4 rounded-md hover:bg-navy-700"
          >
            <FiHeadphones className="mr-2" /> Contactar soporte
          </a>
          <a
            href="/support/chat"
            className="flex items-center justify-center border border-navy-600 text-navy-600 py-2 px-4 rounded-md hover:bg-navy-50"
          >
            <FiMessageSquare className="mr-2" /> Chat en vivo
          </a>
        </div>
      </div>
    </div>
  );
}